/**
 * Inline "Edit layout" mode for the intake form (admin only).
 *
 * Rendered by IntakeForm when an administrator toggles layout editing. The form keeps
 * rendering from a live DRAFT of settings/fieldLayout (see lib/layoutEditor.js), so this
 * is WYSIWYG: every rename, width change, move, hide or delete re-renders the real form
 * immediately. Nothing touches Firestore until "Save layout", which writes through the
 * same adminUpdateSettings callable the Admin → Field Builder page uses — the two editors
 * share one stored document and can never disagree.
 *
 * These components are deliberately presentation-only; all state transitions live in
 * lib/layoutEditor.js where they are unit-tested.
 */

import { useState } from 'react';
import { FIELD_WIDTHS, fieldGridClasses, fieldWidth } from '../lib/fieldGrid';
import {
  FORMAT_BACKGROUNDS,
  FORMAT_BORDERS,
  FORMAT_LABEL_SIZES,
  NOTICE_VARIANTS,
  hasEditableOptions,
  normalizeFormat,
} from '../lib/fieldFormat';
import OptionRowsEditor from './OptionRowsEditor';
import SubFieldRowsEditor from './SubFieldRowsEditor';

const LEVELS = [
  ['required', 'Required'],
  ['soft_required', 'Soft required'],
  ['optional', 'Optional'],
];

/** Sticky toolbar shown while layout editing is active. */
export function LayoutEditBar({ dirty, busy, error, onSave, onDiscard }) {
  return (
    <div className="sticky top-3 z-30 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 shadow-md dark:border-amber-700 dark:bg-amber-950">
      <div className="text-sm font-medium text-amber-900 dark:text-amber-100">
        Editing layout — changes preview live and are saved for everyone.
        {error && <span className="ml-2 text-red-700 dark:text-red-300">{error}</span>}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="rounded border border-amber-400 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:text-amber-100 dark:hover:bg-amber-900"
        >
          {dirty ? 'Discard changes' : 'Exit edit mode'}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || !dirty}
          className="rounded bg-firm-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
        >
          {busy ? 'Saving…' : 'Save layout'}
        </button>
      </div>
    </div>
  );
}

/**
 * Visual-emphasis controls (bold label, label size, background, border). Shared shape
 * with lib/fieldFormat.js; each change patches the whole `format` object so base-field
 * overrides and custom-field records stay symmetric.
 */
/**
 * @param {boolean} textOnly  For a NOTICE banner: its colour and border come from its
 *   variant (Warning / Neutral / Plain), so the background and border selects are hidden
 *   to avoid two controls fighting over the same pixels. The text controls apply to the
 *   notice's message; elsewhere they apply to the field's label.
 */
export function FormatControls({ field, onPatch, textOnly = false }) {
  const format = normalizeFormat(field.format);
  const set = (key, value) => onPatch({ format: { ...format, [key]: value } });
  const name = field.label || field.text || field.path;
  const target = textOnly ? 'text' : 'label';
  return (
    <fieldset className="text-xs text-slate-600 dark:text-slate-300">
      <legend className="mb-0.5">{textOnly ? 'Text formatting' : 'Formatting'}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={format.bold}
            onChange={(event) => set('bold', event.target.checked)}
            aria-label={`Bold ${target} for ${name}`}
          />
          <span className="font-bold">Bold</span>
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={format.italic}
            onChange={(event) => set('italic', event.target.checked)}
            aria-label={`Italic ${target} for ${name}`}
          />
          <span className="italic">Italic</span>
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={format.underline}
            onChange={(event) => set('underline', event.target.checked)}
            aria-label={`Underline ${target} for ${name}`}
          />
          <span className="underline">Underline</span>
        </label>
        <select
          className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
          value={format.labelSize}
          onChange={(event) => set('labelSize', event.target.value)}
          aria-label={`Text size for ${name}`}
        >
          {FORMAT_LABEL_SIZES.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {!textOnly && (
          <>
            <select
              className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
              value={format.background}
              onChange={(event) => set('background', event.target.value)}
              aria-label={`Background for ${name}`}
            >
              {FORMAT_BACKGROUNDS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
              value={format.border}
              onChange={(event) => set('border', event.target.value)}
              aria-label={`Border for ${name}`}
            >
              {FORMAT_BORDERS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </>
        )}
      </div>
    </fieldset>
  );
}

/**
 * Wraps one rendered field with its inline edit controls. The wrapper owns the grid
 * placement (span + row start) so the preview relocates/resizes as the admin edits;
 * the real input underneath stays visible but inert.
 */
export function FieldEditShell({
  field,
  sectionId,
  allSections,
  onPatch,
  onMove,
  onMoveToSection,
  onDelete,
  deleteWarnings = [],
  deleteDisabled = false,
  children,
}) {
  const isNotice = field.type === 'notice';
  const isAttachments = field.type === 'attachments';
  const isRepeatable = field.isCustom && field.type === 'repeatable';
  const widthLocked = isNotice || isAttachments;
  const name = field.label || field.text || field.path;
  // Deleting is two-step. For a built-in field referenced by approval rules or show/hide
  // conditions, the confirmation NAMES what the deletion affects — a governance gate may
  // be removed deliberately, never silently.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div
      className={`${fieldGridClasses(field)} rounded border border-dashed p-2 ${
        field.isActive
          ? 'border-firm-300 dark:border-firm-700'
          : 'border-slate-300 opacity-60 dark:border-slate-600'
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        {isNotice ? (
          <input
            className="min-w-32 flex-1 rounded border border-slate-300 px-2 py-1 dark:border-slate-600"
            value={field.text || ''}
            onChange={(event) => onPatch({ text: event.target.value })}
            title="Notice message"
            aria-label={`Notice message for ${name}`}
          />
        ) : (
          <input
            className="min-w-32 flex-1 rounded border border-slate-300 px-2 py-1 dark:border-slate-600"
            value={field.label || ''}
            onChange={(event) => onPatch({ label: event.target.value })}
            title="Field label"
            aria-label={`Label for ${field.path}`}
          />
        )}
        {isNotice && (
          <select
            className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
            value={field.variant || 'warning'}
            onChange={(event) => onPatch({ variant: event.target.value })}
            title="Notice style"
            aria-label={`Style for ${name}`}
          >
            {NOTICE_VARIANTS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        )}
        <select
          className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
          value={fieldWidth(field)}
          disabled={widthLocked}
          onChange={(event) => onPatch({ width: event.target.value })}
          title={widthLocked ? 'Notices and upload blocks are always full width' : 'Field width'}
          aria-label={`Width for ${name}`}
        >
          {FIELD_WIDTHS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {!isNotice && !isAttachments && (
          <select
            className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
            value={field.requirementOverride || field.requirement || 'optional'}
            onChange={(event) => onPatch({ requirement: event.target.value })}
            title="Requirement level"
            aria-label={`Requirement for ${name}`}
          >
            {LEVELS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        )}
        <select
          className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
          value={sectionId}
          onChange={(event) => onMoveToSection(event.target.value)}
          title="Move to another page"
          aria-label={`Move ${name} to another page`}
        >
          {allSections.map((section) => (
            <option key={section.id} value={section.id}>{section.title}</option>
          ))}
        </select>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            className="rounded px-1.5 py-1 text-slate-500 hover:bg-slate-100 hover:text-firm-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-firm-100"
            title="Move up"
            aria-label={`Move ${name} up`}
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            className="rounded px-1.5 py-1 text-slate-500 hover:bg-slate-100 hover:text-firm-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-firm-100"
            title="Move down"
            aria-label={`Move ${name} down`}
          >
            ↓
          </button>
        </span>
        <label className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={field.isActive}
            onChange={(event) => onPatch({ isActive: event.target.checked })}
            aria-label={`Show ${name} on the form`}
          />
          Shown
        </label>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="text-red-600 hover:underline dark:text-red-400"
          title={
            field.isCustom
              ? 'Delete this custom field permanently'
              : 'Delete this built-in field (restorable from Admin → Field Builder → Deleted fields)'
          }
        >
          Delete
        </button>
      </div>
      {confirmingDelete && (
        <div className="mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">Delete “{name}”?</p>
          {deleteWarnings.length > 0 && (
            <ul className="mt-1 list-disc pl-4">
              {deleteWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          <p className="mt-1">
            {field.isCustom
              ? 'This custom field and its definition are removed permanently — it cannot be restored. Answers already saved on matters are not erased, but they will no longer appear on forms or records with this field’s label.'
              : 'It disappears from the form and the Field Builder. Saved answers are not erased, and you can restore it later from Admin → Field Builder → Deleted fields.'}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(false);
                onDelete();
              }}
              disabled={deleteDisabled}
              title={deleteDisabled ? 'Checking approval-rule references…' : undefined}
              className="rounded bg-red-600 px-3 py-1 font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Delete field
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="rounded border border-red-300 px-3 py-1 hover:bg-red-100 dark:border-red-700 dark:hover:bg-red-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {
        <details className="mb-2 rounded border border-slate-200 p-2 text-xs dark:border-slate-700">
          <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
            {hasEditableOptions(field) ? 'Choices & formatting' : isRepeatable ? 'Columns & formatting' : isNotice ? 'Text formatting' : isAttachments ? 'Help text' : 'Formatting'}
          </summary>
          <div className="mt-2 flex flex-wrap items-start gap-4">
            {isRepeatable && (
              <SubFieldRowsEditor fields={field.fields} onChange={(next) => onPatch({ fields: next })} />
            )}
            {hasEditableOptions(field) && (
              <OptionRowsEditor
                options={field.options}
                minimumOptions={field.type === 'radio' ? 2 : 1}
                autoSlug={false}
                onChange={(next) => onPatch({ options: next })}
              />
            )}
            {!isNotice && (
              <label className="block min-w-64 text-xs text-slate-600 dark:text-slate-300">
                Help text shown under the field
                <input
                  className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                  value={field.helpOverride ?? field.help ?? ''}
                  onChange={(event) => onPatch({ helpText: event.target.value })}
                  aria-label={`Help text for ${name}`}
                />
              </label>
            )}
            {!isAttachments && (
              <FormatControls field={field} onPatch={onPatch} textOnly={isNotice} />
            )}
          </div>
        </details>
      }
      {/* The live preview: real control, inert in edit mode. */}
      <div className="pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
    </div>
  );
}
